import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, CircularProgress, Alert, Button } from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Error from '@mui/icons-material/Error';
import { useTranslation } from 'react-i18next';
import { AI_API_URL } from '../constants';
import { auth } from '../services/firebase';

/**
 * 커스텀 이메일 인증 처리 페이지
 */
const EmailVerificationPage = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleEmailVerification = async () => {
      // ✅ i18n 초기화 대기
      if (!i18n.isInitialized) {
        await new Promise(resolve => {
          const checkInitialized = () => {
            if (i18n.isInitialized) {
              resolve();
            } else {
              setTimeout(checkInitialized, 100);
            }
          };
          checkInitialized();
        });
      }

      const urlParams = new URLSearchParams(location.search);
      const token = urlParams.get('token');

      if (!token) {
        setError(t('emailVerification.errors.invalidLink'));
        setLoading(false);
        return;
      }

      try {
        // 백엔드 API 호출 (AI_API_URL 사용으로 일관성 확보)
        const response = await fetch(`${AI_API_URL}/api/email-auth/verify?token=${token}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setSuccess(true);
          console.log('✅ 이메일 인증 완료:', data);
          
          // Firebase 사용자 정보 강제 새로고침 (이메일 인증 상태 즉시 반영)
          if (auth.currentUser) {
            try {
              await auth.currentUser.reload();
              console.log('✅ Firebase 사용자 정보 새로고침 완료 - emailVerified:', auth.currentUser.emailVerified);
            } catch (reloadError) {
              console.error('❌ Firebase 사용자 정보 새로고침 실패:', reloadError);
            }
          }
          
          // 인증 완료 플래그 설정 (LandingLayout에서 감지용)
          sessionStorage.setItem('emailVerificationSuccess', 'true');
          
          // 3초 후 자동으로 홈페이지로 리다이렉트
          setTimeout(() => {
            navigate('/');
          }, 3000);
        } else {
          setError(data.detail || data.message || t('emailVerification.errors.processingError'));
        }
      } catch (err) {
        console.error('이메일 인증 처리 실패:', err);
        setError(t('emailVerification.errors.serverError'));
      } finally {
        setLoading(false);
      }
    };

    handleEmailVerification();
  }, [location, t, i18n.isInitialized]);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoLogin = () => {
    navigate('/?login=true');
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
          <CircularProgress size={60} sx={{ mb: 2, color: '#667eea' }} />
          <Typography variant="h6" gutterBottom>
            {t('emailVerification.processing')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('emailVerification.pleaseWait')}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      bgcolor="background.default"
      p={2}
    >
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center', maxWidth: 500 }}>
        {success ? (
          <>
            <CheckCircle sx={{ fontSize: 80, color: '#667eea', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ color: '#667eea', fontWeight: 600 }}>
              {t('emailVerification.success.title')}
            </Typography>
            <Typography variant="h6" paragraph sx={{ color: '#333', mb: 3 }}>
              {t('emailVerification.success.subtitle')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ color: '#555', lineHeight: 1.7 }}>
              {t('emailVerification.success.message')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#888', fontStyle: 'italic', mt: 2 }}>
              3초 후 자동으로 홈페이지로 이동합니다...
            </Typography>
            <Box sx={{ 
              mt: 4, 
              display: 'flex', 
              gap: 2, 
              justifyContent: 'center',
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <Button 
                variant="contained" 
                onClick={handleGoHome}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  }
                }}
              >
                {t('emailVerification.success.homeButton')}
              </Button>
              <Button 
                variant="outlined" 
                onClick={handleGoLogin}
                sx={{
                  borderColor: '#667eea',
                  color: '#667eea',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#5a6fd8',
                    color: '#5a6fd8',
                  }
                }}
              >
                {t('emailVerification.success.loginButton')}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Error sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom color="error.main">
              {t('emailVerification.failure.title')}
            </Typography>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Typography variant="body2" paragraph color="text.secondary">
              {t('emailVerification.failure.message')}
            </Typography>
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button variant="contained" onClick={handleGoHome}>
                {t('emailVerification.failure.homeButton')}
              </Button>
              <Button variant="outlined" onClick={handleGoLogin}>
                {t('emailVerification.failure.retryButton')}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default EmailVerificationPage; 